import { useImmer } from 'use-immer'
import { Button, message } from 'antd'
import { isEmpty, set, cloneDeep, pick, unset } from 'lodash'
import { t } from 'helpers/i18n'
import linkTargets from 'options/linkTargets'
import { showError, showClientNotifications } from 'helpers/errors'
import { calculateOrderTotals } from 'helpers/procurement'
import Icon from 'elements/Icon'
import Drawer from 'elements/Drawer'
import FormView from 'containers/Orders/FormView'

function Component(props) {
  const { parentRecord, parentHasUnsavedChanges, selectedRowKeys } = props

  const [state, updateState] = useImmer({})

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  async function handleCopyClick() {
    if (parentHasUnsavedChanges) {
      message.error(t('saveChangesFirst'))
      return
    }

    try {
      setState('loading', true)

      const copied = await props.newItem({}).then((r) => cloneDeep(r.value.data))

      unset(copied, 'id')

      Object.assign(
        copied,
        pick(parentRecord, [
          'requisitionerName',
          'requisitionerPhone',
          'requisitionerEmail',
          'shipToAddressId',
          'billToAddressId',
          'taxRate',
          'scanType',
          'orderCalculationType',
        ])
      )

      const orderItems = parentRecord.orderItems
        .filter((each) => selectedRowKeys.includes(each.id))
        .map((each) => ({
          timeScanned: new Date().toJSON(),
          serverTimeScanned: new Date().toJSON(),
          userName: props.user.userName,
          productId: each.productId,
          barcode: each.barcode,
          inventoryBarcode: each.inventoryBarcode,
          itemNumber: each.itemNumber,
          description: each.description,
          locationId: each.locationId,
          binLocation: each.binLocation,
          manufacturer: each.manufacturer,
          nonStock: props.customer.generalSettings.addNonStockToProducts ? false : each.nonStock,
          price: each.price,
          unitOfMeasure: each.unitOfMeasure,
          packageSize: each.packageSize,
          packageSizeUom: each.packageSizeUom,
          quantityOrdered: each.quantityOrdered,
          packageQuantityOrdered: each.packageQuantityOrdered,
          taxable: each.taxable,
          taxableDescription: each.taxableDescription,
          expedite: each.expedite,
          packagePrice: each.packagePrice,
          packageUnitOfMeasure: each.packageUnitOfMeasure,
          leadTime: each.leadTime,
          nonInventory: each.nonInventory,
          accountNumber: each.accountNumber,
          reworkType: each.reworkType,
          isPersonalPurchase: each.isPersonalPurchase,
          receiveToOnHand2: each.receiveToOnHand2,
          supplierId: each.supplierId,
          supplierItemNumber: each.supplierItemNumber,
          deliveryDate: each.deliveryDate,
          trackingNumber: each.trackingNumber,
          carrier: each.carrier,
          locationBarcode: each.locationBarcode,
          locationName: each.locationName,
          supplierName: each.supplierName,
          scanType: each.scanType,
          orderCalculationType: each.orderCalculationType,
          procurementType: 'Purchase',
          sourceCatalogName: each.sourceCatalogName,
          tagA: each.tagA,
          tagB: each.tagB,
          tagC: each.tagC,
          tagD: each.tagD,
          tagE: each.tagE,
        }))

      const supplierIds = orderItems
        .filter((each) => Boolean(each.supplierId) && each.quantityOrdered > 0)
        .map((each) => each.supplierId)

      const suppliers = await Promise.all(
        [...new Set(supplierIds)].map((each) => props.getSupplier(each).then((r) => r.value.data))
      )

      const purchaseOrderNumbers = await Promise.all(
        supplierIds.map((supplierId) =>
          props.generatePurchaseOrderNumber({ orderId: 0, supplierId }).then((each) => ({
            supplierId,
            ...each.value.data,
          }))
        )
      )

      const orderSuppliers = suppliers.map((each) => ({
        adjustments: 0,
        confirmedDescription: 'No',
        freight: 0,
        purchaseOrderNumber: '',
        shippingInstructions: each.shippingInstructions,
        supplierId: each.id,
        supplierName: each.name,
      }))

      Object.assign(copied, calculateOrderTotals({ customer: props.customer, item: copied, orderItems }))

      orderSuppliers.forEach((each) => {
        try {
          each.purchaseOrderNumber = purchaseOrderNumbers.find(
            (one) => one.supplierId === each.supplierId
          ).purchaseOrderNumber
        } catch (error) {
          console.warn(error)
        }
      })

      const response = await props.saveOrder({
        order: copied,
        orderItems: { creating: orderItems },
        orderSuppliers: { creating: orderSuppliers },
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      updateState((draft) => {
        draft.linkTargetRecord = response.value.data.items[0]
        draft.drawerVisible = true
      })
    } catch (error) {
      showError({ error })
    } finally {
      setState('loading', false)
    }
  }

  return (
    <>
      <Button onClick={handleCopyClick} disabled={isEmpty(selectedRowKeys)} loading={state.loading}>
        {!state.loading && <Icon type="ContentCopy" />} {t('copy')}
      </Button>
      <Drawer
        title={t('copy')}
        size={linkTargets.orderRecord.formSize}
        visible={state.drawerVisible}
        onClose={() => setState('drawerVisible', false)}
      >
        <FormView
          linkTargetRecord={state.linkTargetRecord}
          onSave={() => {}}
          onSaveAndClose={() => setState('drawerVisible', false)}
          onCancel={() => setState('drawerVisible', false)}
        />
      </Drawer>
    </>
  )
}

export default Component
